import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AccountSettingsModal from '../components/AccountSettingsModal';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [featureRequest, setFeatureRequest] = useState({ name: '', email: '', company: '', request: '' });
  const [sendingRequest, setSendingRequest] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const handleFeatureRequestSubmit = async (e) => {
    e.preventDefault();
    if (!featureRequest.request) return;
    setSendingRequest(true);
    try {
      await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: featureRequest.name,
          email: featureRequest.email,
          phone: '',
          message: `[Feature Request]\n\nCompany: ${featureRequest.company || 'N/A'}\n\nRequest:\n${featureRequest.request}`
        })
      });
      setRequestSent(true);
      setFeatureRequest({ name: '', email: '', company: '', request: '' });
      setTimeout(() => setRequestSent(false), 3000);
    } catch {
      alert('Failed to send request. Please try again.');
    }
    setSendingRequest(false);
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const secondaryTools = [
    { id: 'waste-room', title: 'Waste Room Builder', desc: 'Model waste rooms with real equipment', route: '/waste-room-builder', icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z' },
    { id: 'vehicle-access', title: 'Vehicle Access Planner', desc: 'Swept path assessment for waste trucks', route: '/vehicle-access', icon: 'M1 3h15v13H1V3zM16 8h4l3 4v5h-7V8z' },
    { id: 'see-engine', title: 'SEE Decision Engine', desc: 'Statement of Environmental Effects', route: '/see-generator', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', comingSoon: true },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <SEO title="Dashboard" description="PlanGen Consulting project dashboard" noIndex={true} />

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-40" data-testid="dashboard-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="text-2xl font-bold tracking-tight text-[#0A2540]" data-testid="dashboard-logo">
            PlanGen<span className="text-blue-600">Consulting</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-slate-500">{user?.full_name || user?.email}</span>
            <button onClick={() => navigate('/guidelines')} className="text-xs text-slate-500 hover:text-[#0A2540] transition-colors">Guidelines</button>
            {user?.is_admin && (
              <button onClick={() => navigate('/admin')} className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors" data-testid="admin-link">Admin</button>
            )}
            <button onClick={() => setShowAccountSettings(true)} className="text-xs text-slate-500 hover:text-[#0A2540] transition-colors" data-testid="account-settings-btn">Account</button>
            <button onClick={handleLogout} className="text-xs text-slate-400 hover:text-red-500 transition-colors">Logout</button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-[#0A2540]" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Your waste management planning toolkit</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left — Main content */}
          <div className="flex-1 space-y-5">
            {/* Primary: My Plans */}
            <div
              onClick={() => navigate('/my-plans')}
              className="group relative bg-gradient-to-b from-[#0A2540] via-[#0f3460] to-[#1a4a7a] rounded-2xl p-7 cursor-pointer hover:shadow-[0_12px_40px_rgb(10,37,64,0.3)] transition-all duration-300 overflow-hidden"
              data-testid="tool-card-my-plans"
            >
              {/* Texture overlay */}
              <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
              <div className="absolute top-0 right-0 w-[250px] h-[250px] bg-blue-400/8 rounded-full blur-[80px]" />
              <div className="absolute bottom-0 left-[20%] w-[200px] h-[150px] bg-sky-400/6 rounded-full blur-[60px]" />
              <div className="relative flex items-center gap-5">
                <div className="w-14 h-14 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white mb-1">My Plans</h2>
                  <p className="text-sm text-slate-300">Create and manage your Waste Management Plans — OpWMP, D&amp;CWMP, CMP</p>
                </div>
                <svg className="w-5 h-5 text-white/40 ml-auto flex-shrink-0 group-hover:translate-x-1 group-hover:text-white/70 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>

            {/* Secondary: Tool cards — horizontal row */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {secondaryTools.map((tool) => (
                <div
                  key={tool.id}
                  onClick={() => navigate(tool.route)}
                  className={`group bg-white rounded-xl border border-slate-100 p-4 cursor-pointer hover:shadow-[0_4px_20px_rgb(0,0,0,0.06)] hover:border-blue-200 transition-all duration-200 ${tool.comingSoon ? 'opacity-60' : ''}`}
                  data-testid={`tool-card-${tool.id}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center mb-3">
                    <svg className="w-[18px] h-[18px] text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={tool.icon} />
                      {tool.id === 'vehicle-access' && (
                        <>
                          <circle cx="5.5" cy="18.5" r="2.5" strokeWidth={1.5} />
                          <circle cx="18.5" cy="18.5" r="2.5" strokeWidth={1.5} />
                        </>
                      )}
                    </svg>
                  </div>
                  <h3 className="text-sm font-bold text-[#0A2540] mb-0.5 group-hover:text-blue-700 transition-colors">{tool.title}</h3>
                  <p className="text-[11px] text-slate-500 leading-relaxed">{tool.desc}</p>
                  {tool.comingSoon && (
                    <span className="inline-block mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">Coming Soon</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right — Support/Feature Request (compact) */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-100 p-5 sticky top-20" data-testid="feature-request-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-sm font-bold text-[#0A2540]">Support</h3>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed mb-3">Need a feature or have an issue? Let us know.</p>
              {requestSent ? (
                <div className="flex items-center gap-2 py-2">
                  <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  <p className="text-xs font-medium text-emerald-700">Sent!</p>
                </div>
              ) : (
                <form onSubmit={handleFeatureRequestSubmit} className="space-y-2">
                  <input type="text" placeholder="Name" value={featureRequest.name} onChange={(e) => setFeatureRequest({...featureRequest, name: e.target.value})}
                    className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all" data-testid="feature-request-name" />
                  <input type="email" placeholder="Email" value={featureRequest.email} onChange={(e) => setFeatureRequest({...featureRequest, email: e.target.value})}
                    className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all" data-testid="feature-request-email" />
                  <textarea placeholder="Your request..." rows={2} value={featureRequest.request} onChange={(e) => setFeatureRequest({...featureRequest, request: e.target.value})}
                    className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all resize-none" data-testid="feature-request-message" />
                  <button type="submit" disabled={sendingRequest}
                    className="w-full py-1.5 bg-[#0A2540] text-white text-xs font-semibold rounded-lg hover:bg-black transition-colors disabled:opacity-50" data-testid="feature-request-submit">
                    {sendingRequest ? 'Sending...' : 'Send'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <AccountSettingsModal
        isOpen={showAccountSettings}
        onClose={() => setShowAccountSettings(false)}
        user={user}
        onUserUpdate={(updatedUser) => { setUser(updatedUser); setShowAccountSettings(false); }}
      />
    </div>
  );
};

export default Dashboard;
